<template>
    <BasicModal
        :title="modalTitle"
        :wide="row.type !== 'custom'"
        @close="$emit('close')"
    >
        <div v-if="headerImage" class="row-detail-image">
            <ExternalImage :src="headerImage.src" :alt="row.name" />
            <a
                v-if="headerImage.authorName"
                :href="headerImage.authorUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="row-detail-image__credit"
            >
                <span class="material-symbols-outlined">person</span>
                {{ headerImage.authorName }}
            </a>
        </div>

        <div class="form-field">
            <label for="row-detail-name">Name</label>
            <input
                id="row-detail-name"
                v-model="name"
                type="text"
                :disabled="!row.is_owner"
                @blur="onNameBlur"
            />
            <p v-if="!row.is_owner" class="row-detail-link-note">
                {{ nameHint }}
            </p>
        </div>

        <p v-if="loadingEntity" class="row-detail-link-note">Loading…</p>
        <ScheduleRowInfo
            v-else
            :row="row"
            :entity="entity"
            :currency="currency"
        />

        <div v-if="row.is_owner" class="row-detail-switch">
            <button
                v-if="!switching"
                type="button"
                class="link-button"
                @click="switching = true"
            >
                {{ switchLabel }}
            </button>
            <AddScheduleRow
                v-else
                :year="year"
                :excluded="excluded"
                @add="onReplace"
            />
        </div>

        <div v-if="sharePending" class="form-field">
            <ShareFriends :item-id="row.id" item-type="schedule" />
        </div>
        <div class="actions">
            <template v-if="deletePending">
                <div class="confirmation">{{ confirmMessage }}</div>
                <Bttn class="outlined" @click="deletePending = false">
                    Cancel
                </Bttn>
                <Bttn @click="onDeleteConfirmed">{{ confirmLabel }}</Bttn>
            </template>
            <template v-else-if="sharePending">
                <Bttn class="outlined" @click="sharePending = false">
                    Done
                </Bttn>
            </template>
            <template v-else>
                <Bttn
                    v-if="row.is_owner"
                    class="outlined"
                    icon="share"
                    @click="sharePending = true"
                >
                    Share
                </Bttn>
                <Bttn
                    class="outlined"
                    icon="delete"
                    @click="deletePending = true"
                >
                    {{ row.is_owner ? "Delete" : "Leave plan" }}
                </Bttn>
            </template>
        </div>
    </BasicModal>
</template>

<script>
import BasicModal from "js/components/modals/BasicModal";
import AddScheduleRow from "js/components/forms/AddScheduleRow";
import ShareFriends from "js/components/common/ShareFriends";
import ScheduleRowInfo from "js/components/blocks/ScheduleRowInfo";
import { cropUrl } from "js/lib/pexels";
import { fetch, handleResponse } from "js/lib/server";
import { useSavedIdeaStore } from "js/stores/savedIdeaStore";
import { useUserStore } from "js/stores/userStore";
import ExternalImage from "js/components/common/ExternalImage.vue";

export default {
    name: "RowDetailModal",
    components: {
        BasicModal,
        AddScheduleRow,
        ShareFriends,
        ScheduleRowInfo,
        ExternalImage,
    },
    props: {
        row: {
            type: Object,
            required: true,
        },
        year: {
            type: Number,
            required: true,
        },
        excluded: {
            type: Array,
            default: () => [],
        },
    },
    emits: ["close", "remove", "update"],
    setup() {
        return { userStore: useUserStore() };
    },
    data() {
        return {
            name: this.row.name,
            entity: null,
            loadingEntity: this.row.type !== "custom",
            deletePending: false,
            sharePending: false,
            switching: false,
            nameEdited: false,
        };
    },
    computed: {
        currency() {
            return this.userStore?.user?.currency ?? "$";
        },
        hasFriends() {
            return (this.row.participants ?? []).length > 0;
        },
        confirmMessage() {
            if (!this.row.is_owner) {
                return "Remove yourself from this plan? It will stay for its owner and other participants.";
            }
            if (this.hasFriends) {
                return "This will delete the plan for everyone. Continue?";
            }
            return `Remove "${this.row.name}" from the schedule?`;
        },
        nameHint() {
            return this.row.owner_name
                ? `Only ${this.row.owner_name}, the plan's owner, can rename it.`
                : "Only the plan's owner can rename it.";
        },
        confirmLabel() {
            return this.row.is_owner ? "Remove" : "Leave";
        },
        modalTitle() {
            return `Edit: ${this.row.name}`;
        },
        switchLabel() {
            return this.row.type === "custom"
                ? "Link to a trip or idea…"
                : "Change to a different trip or idea…";
        },
        headerImage() {
            if (this.row.type === "trip") {
                const src =
                    this.entity?.user_image_url ??
                    this.entity?.locations?.find((l) => l.image_url)?.image_url;
                return src ? { src, authorName: null } : null;
            }
            const photo = this.entity?.pexel_images?.[0];
            if (this.row.type === "idea" && photo) {
                return {
                    src: photo[2] ?? cropUrl(photo[0], 1200, 675),
                    authorName: photo[3],
                    authorUrl: photo[4],
                };
            }
            return null;
        },
    },
    watch: {
        "row.name"(newName) {
            this.name = newName;
        },
    },
    async mounted() {
        await this.loadEntity();
        this.loadingEntity = false;
    },
    methods: {
        async loadEntity() {
            if (this.row.type === "idea") {
                const savedIdeaStore = useSavedIdeaStore();
                await savedIdeaStore.load();
                this.entity =
                    savedIdeaStore.list.find(
                        (idea) => idea.id === this.row.itemable_id
                    ) ?? null;
                return;
            }
            if (this.row.type === "trip") {
                try {
                    this.entity = await fetch(
                        `/api/trips/${this.row.itemable_id}`,
                        { params: { "with-locations": 1 } }
                    ).then(handleResponse);
                } catch (err) {
                    console.error(err);
                    this.entity = null;
                }
            }
        },
        onNameBlur() {
            const trimmed = this.name.trim();
            if (trimmed === this.row.name) {
                return;
            }
            this.nameEdited = true;
            this.$emit("update", { name: trimmed || null });
        },
        onReplace(seed) {
            this.$emit("update", {
                type: seed.type,
                itemable_id: seed.itemable_id,
                ...(this.row.type !== "custom" && this.nameEdited
                    ? {}
                    : { name: seed.name }),
            });
            this.switching = false;
        },
        onDeleteConfirmed() {
            this.$emit("remove", this.row.id);
            this.deletePending = false;
            this.$emit("close");
        },
    },
};
</script>
